import { useCallback, useState } from 'react';
import {
  Button,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  SaveHint,
  Select,
  Skeleton,
  useAutoSave,
} from '../../components/ui';
import { useTapPeople } from '../projects/hooks';
import { KeyValueRow } from './KeyValueRows';
import {
  useCompanies,
  useCreateSalesTask,
  useDeleteSalesTask,
  useSalesTasks,
  useUpdateSalesTask,
} from './hooks';
import {
  useCompletedLeadFollowups,
  useLeadFollowups,
  useUpdateLeadFollowup,
} from './leadFollowups';
import { todayIso } from './trackerFormat';
import { filterTrackerTasks, fromFollowup, fromSalesTask } from './trackerTasks';
import type { TrackerTask } from './trackerTasks';
import { TASK_FILTER_LABELS, leadName, salesErrorMessage } from './types';
import type { Company, Lead, SalesTask, TaskFilter } from './types';

export function TaskFormDrawer({
  task,
  leads,
  companies,
  initialCompanyId,
  onClose,
}: {
  task: SalesTask | null; // null = create
  leads: Lead[];
  companies: Company[];
  initialCompanyId?: string;
  onClose: () => void;
}) {
  const create = useCreateSalesTask();
  const update = useUpdateSalesTask();
  const remove = useDeleteSalesTask();
  const peopleQuery = useTapPeople(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState(task?.title ?? '');
  const [dueOn, setDueOn] = useState(task?.due_on ?? '');
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to ?? '');
  const [leadId, setLeadId] = useState(task?.lead_id ?? '');
  const [companyId, setCompanyId] = useState(task?.company_id ?? initialCompanyId ?? '');

  const people = peopleQuery.data ?? [];
  const editing = task !== null;
  const pending = create.isPending || remove.isPending;
  const sortedLeads = [...leads].sort((a, b) => leadName(a).localeCompare(leadName(b)));

  /** One writer for every field on an existing task; each saves on its own. */
  const commit = useCallback(
    (changes: Record<string, string | null>) =>
      new Promise((resolve, reject) => {
        if (task === null) {
          resolve(undefined);
          return;
        }
        setError('');
        update.mutate(
          { taskId: task.id, changes },
          {
            onSuccess: resolve,
            onError: (cause) => {
              setError(salesErrorMessage(cause.message));
              reject(cause);
            },
          },
        );
      }),
    [task, update],
  );

  const titleSave = useAutoSave<string>(
    useCallback(
      (next: string) =>
        next.trim() === ''
          ? Promise.reject(new Error('Give the task a title first.'))
          : commit({ title: next.trim() }),
      [commit],
    ),
  );
  const dueSave = useAutoSave<string>(
    useCallback((next: string) => commit({ due_on: next === '' ? null : next }), [commit]),
  );
  const assigneeSave = useAutoSave<string>(
    useCallback((next: string) => commit({ assigned_to: next === '' ? null : next }), [commit]),
  );
  const leadSave = useAutoSave<string>(
    useCallback((next: string) => commit({ lead_id: next === '' ? null : next }), [commit]),
  );
  const companySave = useAutoSave<string>(
    useCallback((next: string) => commit({ company_id: next === '' ? null : next }), [commit]),
  );

  function createTask() {
    if (title.trim() === '') {
      setError('Give the task a title first.');
      return;
    }
    setError('');
    create.mutate(
      {
        title: title.trim(),
        due_on: dueOn === '' ? null : dueOn,
        assigned_to: assignedTo === '' ? null : assignedTo,
        lead_id: leadId === '' ? null : leadId,
        company_id: companyId === '' ? null : companyId,
      },
      {
        onSuccess: onClose,
        onError: (cause: Error) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  const peopleOptions = [
    { value: '', label: '—' },
    ...people.map((person) => ({ value: person.id, label: person.display_name })),
  ];
  const leadOptions = [
    { value: '', label: '—' },
    ...sortedLeads.map((lead) => ({ value: lead.id, label: leadName(lead) })),
  ];
  const companyOptions = [
    { value: '', label: '—' },
    ...companies.map((company) => ({ value: company.id, label: company.name })),
  ];

  return (
    <Drawer
      open
      title={editing ? 'Edit task' : 'New task'}
      onClose={onClose}
      menuItems={
        editing
          ? [
              {
                label: 'Delete task',
                danger: true,
                onSelect: () => {
                  setError('');
                  remove.mutate(task.id, {
                    onSuccess: onClose,
                    onError: (cause) => setError(salesErrorMessage(cause.message)),
                  });
                },
              },
            ]
          : undefined
      }
      footer={
        editing ? undefined : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={createTask} disabled={pending}>
              {pending ? 'Saving…' : 'Create task'}
            </Button>
          </>
        )
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      {editing ? (
        <div className="kv-list">
          <KeyValueRow
            label="Title"
            hint={<SaveHint state={titleSave.state} error={titleSave.error} />}
          >
            <input
              className="kv-input"
              type="text"
              aria-label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              onBlur={() => titleSave.save(title)}
            />
          </KeyValueRow>
          <KeyValueRow label="Due" hint={<SaveHint state={dueSave.state} error={dueSave.error} />}>
            <input
              className="kv-input"
              type="date"
              aria-label="Due"
              value={dueOn}
              onChange={(event) => {
                setDueOn(event.target.value);
                dueSave.save(event.target.value);
              }}
            />
          </KeyValueRow>
          <KeyValueRow
            label="Assigned to"
            hint={<SaveHint state={assigneeSave.state} error={assigneeSave.error} />}
          >
            <select
              className="kv-input"
              aria-label="Assigned to"
              value={assignedTo}
              onChange={(event) => {
                setAssignedTo(event.target.value);
                assigneeSave.save(event.target.value);
              }}
            >
              {peopleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </KeyValueRow>
          <KeyValueRow
            label="Linked lead"
            hint={<SaveHint state={leadSave.state} error={leadSave.error} />}
          >
            <select
              className="kv-input"
              aria-label="Linked lead"
              value={leadId}
              onChange={(event) => {
                setLeadId(event.target.value);
                leadSave.save(event.target.value);
              }}
            >
              {leadOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </KeyValueRow>
          <KeyValueRow
            label="Linked company"
            hint={<SaveHint state={companySave.state} error={companySave.error} />}
          >
            <select
              className="kv-input"
              aria-label="Linked company"
              value={companyId}
              onChange={(event) => {
                setCompanyId(event.target.value);
                companySave.save(event.target.value);
              }}
            >
              {companyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </KeyValueRow>
        </div>
      ) : (
        <>
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input type="date" label="Due" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          <Select
            label="Assigned to"
            value={assignedTo}
            options={peopleOptions}
            onChange={(e) => setAssignedTo(e.target.value)}
          />
          <Select
            label="Linked lead"
            value={leadId}
            options={leadOptions}
            onChange={(e) => setLeadId(e.target.value)}
          />
          <Select
            label="Linked company"
            value={companyId}
            options={companyOptions}
            onChange={(e) => setCompanyId(e.target.value)}
          />
        </>
      )}
    </Drawer>
  );
}

export function TasksView({ leads }: { leads: Lead[] }) {
  const tasksQuery = useSalesTasks();
  const companiesQuery = useCompanies();
  const peopleQuery = useTapPeople(true);
  const update = useUpdateSalesTask();
  // The other half of the list. Follow-ups are rows in `tasks`, not
  // `sales_tasks`, and until this drop nothing on any screen could reach one.
  const followupsQuery = useLeadFollowups();
  const doneFollowupsQuery = useCompletedLeadFollowups();
  const updateFollowup = useUpdateLeadFollowup();
  const [filter, setFilter] = useState<TaskFilter>('all');
  const [editing, setEditing] = useState<SalesTask | 'new' | null>(null);
  const [reschedulingId, setReschedulingId] = useState('');
  const [error, setError] = useState('');

  if (tasksQuery.isPending) return <Skeleton lines={4} label="Loading tasks" />;

  const salesTasks = tasksQuery.data ?? [];
  const companies = companiesQuery.data ?? [];
  const people = peopleQuery.data ?? [];
  const today = todayIso();
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const companyById = new Map(companies.map((c) => [c.id, c]));
  const personName = (id: string | null) =>
    id === null ? '' : (people.find((p) => p.id === id)?.display_name ?? '');

  const merged = [
    ...salesTasks.map(fromSalesTask),
    ...(followupsQuery.data ?? []).map(fromFollowup),
  ];
  const visible = filterTrackerTasks(merged, filter, today);
  const completed = [
    ...salesTasks.filter((task) => task.status === 'done').map(fromSalesTask),
    ...(doneFollowupsQuery.data ?? []).map(fromFollowup),
  ]
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
    .slice(0, 20);

  function linkedTo(task: TrackerTask): string {
    const parts: string[] = [];
    const lead = task.leadId === null ? undefined : leadById.get(task.leadId);
    if (lead !== undefined) parts.push(leadName(lead));
    const company = task.companyId === null ? undefined : companyById.get(task.companyId);
    if (company !== undefined) parts.push(company.name);
    return parts.length === 0 ? 'general' : parts.join(' · ');
  }

  function dueLabel(task: TrackerTask): { text: string; tone: 'danger' | 'warning' | 'plain' } {
    if (task.dueOn === null) return { text: 'No date', tone: 'plain' };
    if (task.dueOn < today) return { text: `${task.dueOn} · overdue`, tone: 'danger' };
    if (task.dueOn === today) return { text: `${task.dueOn} · today`, tone: 'warning' };
    return { text: task.dueOn, tone: 'plain' };
  }

  function setDone(task: TrackerTask, done: boolean) {
    setError('');
    if (task.origin === 'followup') {
      updateFollowup.mutate(
        { taskId: task.id, done },
        { onError: (cause) => setError(salesErrorMessage(cause.message)) },
      );
      return;
    }
    update.mutate(
      {
        taskId: task.id,
        changes: done
          ? { status: 'done', completed_at: new Date().toISOString() }
          : { status: 'open', completed_at: null },
      },
      { onError: (cause) => setError(salesErrorMessage(cause.message)) },
    );
  }

  function taskRow(task: TrackerTask, done: boolean) {
    const due = dueLabel(task);
    return (
      <tr key={`${task.origin}-${task.id}`}>
        <td>
          <input
            type="checkbox"
            checked={done}
            aria-label={`${done ? 'Reopen' : 'Complete'} ${task.title}`}
            onChange={() => setDone(task, !done)}
          />
        </td>
        <td
          style={{
            color:
              done || due.tone === 'plain'
                ? 'var(--text-secondary)'
                : due.tone === 'danger'
                  ? 'var(--state-danger)'
                  : 'var(--state-warning)',
            fontWeight:
              due.tone === 'plain' ? 'var(--font-weight-regular)' : 'var(--font-weight-semibold)',
            whiteSpace: 'nowrap',
          }}
        >
          {due.text}
        </td>
        <td
          style={done ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : undefined}
        >
          {task.title}
        </td>
        <td style={{ color: 'var(--text-secondary)' }}>{linkedTo(task)}</td>
        <td style={{ color: 'var(--text-secondary)' }}>
          {task.origin === 'followup' && !done ? (
            <Select
              label=""
              value={task.assigneeId ?? ''}
              onChange={(event) => {
                setError('');
                updateFollowup.mutate(
                  { taskId: task.id, assigneeId: event.target.value },
                  { onError: (cause) => setError(salesErrorMessage(cause.message)) },
                );
              }}
              options={people.map((person) => ({
                value: person.id,
                label: person.display_name === '' ? person.email : person.display_name,
              }))}
            />
          ) : (
            personName(task.assigneeId)
          )}
        </td>
        <td>
          {task.origin === 'sales' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const original = salesTasks.find((row) => row.id === task.id);
                if (original !== undefined) setEditing(original);
              }}
            >
              Edit
            </Button>
          ) : done ? null : reschedulingId === task.id ? (
            <Input
              label=""
              type="date"
              value={task.dueOn ?? ''}
              onChange={(event) => {
                setError('');
                updateFollowup.mutate(
                  { taskId: task.id, dueDate: event.target.value },
                  {
                    onSuccess: () => setReschedulingId(''),
                    onError: (cause) => setError(salesErrorMessage(cause.message)),
                  },
                );
              }}
            />
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setReschedulingId(task.id)}>
              Reschedule
            </Button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {(Object.keys(TASK_FILTER_LABELS) as TaskFilter[]).map((key) => (
          <Button
            key={key}
            variant={filter === key ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setFilter(key)}
          >
            {TASK_FILTER_LABELS[key]} ({filterTrackerTasks(merged, key, today).length})
          </Button>
        ))}
        <span style={{ flex: 1 }} />
        <Button onClick={() => setEditing('new')}>New task</Button>
      </div>
      {error !== '' && <InlineError>{error}</InlineError>}
      {visible.length === 0 ? (
        <EmptyState title="No open tasks here" description="Different filter, or add a task." />
      ) : (
        <div className="tracker-table-scroll">
          <table className="tracker-table">
            <thead>
              <tr>
                <th aria-label="Done" />
                <th>Due</th>
                <th>Task</th>
                <th>Linked to</th>
                <th>Assigned</th>
                <th aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>{visible.map((task) => taskRow(task, false))}</tbody>
          </table>
        </div>
      )}

      {completed.length > 0 && (
        <div style={{ marginTop: 'var(--space-8)' }}>
          <p className="tracker-dashboard-card-title">Completed (last 20)</p>
          <div className="tracker-table-scroll">
            <table className="tracker-table">
              <tbody>{completed.map((task) => taskRow(task, true))}</tbody>
            </table>
          </div>
        </div>
      )}

      {editing !== null && (
        <TaskFormDrawer
          task={editing === 'new' ? null : editing}
          leads={leads}
          companies={companies}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ── LinkedIn ──────────────────────────────────────────────────────────────────

/** Comma-separated day notes as quiet tag chips (redesign spec). */
